// src/pages/HomePage.tsx

import { FC } from 'react';
import styles from '../css/HomePage.module.css';
import { motion, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
    FiTool, FiZap, FiLayers, FiArrowRight,
    FiDatabase, FiTrendingUp, FiCheck
} from 'react-icons/fi'; 
import { useMediaQuery } from '../hooks/useMediaQuery'; 

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';
// Removed VentureCard import to use inline styling for new layout

// Images
import HeroImg from '../assets/images/HeroImage.png';
import Image1 from '../assets/images/HomepageImage1.jpeg';
import Image2 from '../assets/images/HomepageImage2.png';

const HomePage: FC = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const ICON_SIZE = 24; 
    const ICON_STROKE = 1.5;
    
    // Variants optimized for reduced motion preference
    const containerVariants: Variants = {
        visible: { 
            transition: { 
                staggerChildren: isMobile ? 0.05 : 0.1,
                delayChildren: 0.05
            } 
        },
    };

    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
        visible: { 
            opacity: 1, 
            y: 0, 
            filter: 'blur(0px)',
            transition: { duration: 0.6, ease: "easeOut" } 
        },
    };

    const imageVariants: Variants = {
        hidden: { opacity: 0, scale: 0.98, filter: "blur(10px)" }, 
        visible: { 
            opacity: 1, 
            scale: 1, 
            filter: "blur(0px)",
            transition: { duration: 1, ease: "easeOut" } 
        },
    };

    const floatVariant: Variants = {
        animate: {
            y: [0, -15, 0],
            transition: { duration: 6, ease: "easeInOut", repeat: Infinity }
        }
    };

    const capabilities = [
        {
            icon: <FiDatabase size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
            title: "Data Governance",
            description: "Single sources of truth with robust security frameworks for regulated sectors.",
        },
        {
            icon: <FiZap size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
            title: "Predictive ML",
            description: "Proprietary AI models for forecasting, risk assessment, and classification.",
        },
        {
            icon: <FiTrendingUp size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
            title: "Hyperscale Arch",
            description: "Cloud-native microservices capable of processing billions of transactions.",
        },
    ];

    const ventures = [
        { 
            title: "OTA ERP", 
            tagline: "Unified operational platforms.", 
            icon: <FiTool size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
            link: "/portfolio", 
            isExternal: false
        },
        { 
            title: "Future AI", 
            tagline: "LegalTech Intelligence.", 
            icon: <FiZap size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
            link: "/portfolio", 
            isExternal: false
        },
        { 
            title: "Talent & Growth", 
            tagline: "Industry-embedded experts.", 
            icon: <FiLayers size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
            link: "/careers", 
            isExternal: false
        },
    ];

    return (
        <div className={styles.homePage}>
            
            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.noiseOverlay} aria-hidden="true"></div>
                <div className={styles.container}>
                    <div className={styles.heroGrid}>
                        <div className={styles.heroContent}>
                            <motion.div 
                                initial="hidden"
                                animate="visible"
                                variants={containerVariants}
                            >
                                <motion.div variants={itemVariants} className={styles.badgeWrapper}>
                                    <span className={styles.badgeDot}></span>
                                    <span className={styles.preTitle}>OTA TECH INC. • SYSTEM INTEGRITY</span>
                                </motion.div>
                                
                                <motion.h1 className={styles.heroTitle} variants={itemVariants}>
                                    Building the <br/> 
                                    <span className={styles.titleHighlight}>Foundation</span> of <br/> 
                                    Tomorrow.
                                </motion.h1>
                                
                                <motion.p className={styles.heroSubtitle} variants={itemVariants}>
                                    We engineer intelligent, scalable software solutions that solve foundational business complexity.
                                </motion.p>
                                
                                <motion.div className={styles.heroActions} variants={itemVariants}>
                                    <Link to="/investors" className={styles.ctaPrimary}>
                                        Investor Relations
                                    </Link>
                                    <Link to="/portfolio" className={styles.ctaSecondary}>
                                        Explore Ventures 
                                    </Link>
                                </motion.div>
                            </motion.div>
                        </div>

                        {/* 2. HERO IMAGE (Conditional Animation) */}
                        <motion.div 
                            className={styles.heroImageColumn}
                            initial="hidden"
                            animate="visible"
                            variants={imageVariants}
                        >
                            <motion.div 
                                className={styles.imageGlowContainer}
                                variants={!isMobile ? floatVariant : {}} 
                                animate="animate"
                            >
                                <div className={styles.ambientGlow}></div>
                                <img 
                                    src={HeroImg} 
                                    alt="OTA Tech Platform Dashboard" 
                                    className={styles.heroImg} 
                                    width="800"
                                    height="600"
                                />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 3. VISION SECTION */}
            <AnimatedSection className={styles.visionSection}>
                <div className={styles.container}>
                    <motion.div className={styles.visionContent} variants={itemVariants}>
                        <div className={styles.visionDivider}></div>
                        <motion.h2 className={styles.visionStatement}>
                            Our mission is to eliminate the fragmentation caused by rigid legacy systems. We build platforms that deliver <span className={styles.textHighlight}>true operational agility.</span>
                        </motion.h2>
                        <div className={styles.visionMeta}>
                            <span>— The OTA TECH Difference</span>
                        </div>
                    </motion.div>
                </div>
            </AnimatedSection>
            
            {/* 4. CAPABILITIES */}
            <AnimatedSection className={styles.capabilitiesSection}>
                <div className={styles.container}>
                    <motion.div className={styles.sectionHeader} variants={itemVariants}>
                         <span className={styles.sectionTag}>CORE COMPETENCIES</span>
                         <h3 className={styles.sectionTitleSmall}>Technical Capabilities</h3>
                    </motion.div>

                    <motion.div className={styles.capGrid} variants={containerVariants}>
                        {capabilities.map((cap, index) => (
                            <motion.div className={styles.capCard} key={index} variants={itemVariants}>
                                <div className={styles.capIconWrapper}>
                                    {cap.icon}
                                </div>
                                <div className={styles.capContent}>
                                    <h3 className={styles.capTitle}>{cap.title}</h3>
                                    <p className={styles.capDescription}>{cap.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>
            
            {/* 5. THE EDGE SECTION */}
            <section className={styles.edgeSection}>
                <div className={styles.container}>
                    <div className={styles.splitGrid}>
                        <motion.div 
                            className={styles.splitContent}
                            initial="hidden"
                            whileInView="visible"
                            variants={containerVariants}
                            viewport={{ once: true, margin: "-100px" }}
                        >
                            <motion.span variants={itemVariants} className={styles.sectionTag}>
                                INTELLIGENCE EDGE
                            </motion.span>
                            
                            <motion.h2 className={styles.splitTitle} variants={itemVariants}>
                                Financial & Legal <br />
                                <span className={styles.titleGrey}>Embedded Systems.</span>
                            </motion.h2>
                            
                            <motion.p className={styles.splitText} variants={itemVariants}>
                                We operate at the intersection of high finance, legal compliance, and deep technology.
                            </motion.p>
                            
                            <motion.ul className={styles.featureList} variants={containerVariants}>
                                <motion.li variants={itemVariants}><FiCheck className={styles.checkIcon}/> FinTech compliance optimization</motion.li>
                                <motion.li variants={itemVariants}><FiCheck className={styles.checkIcon}/> LegalTech automation</motion.li>
                                <motion.li variants={itemVariants}><FiCheck className={styles.checkIcon}/> Chartered Accountant-led design</motion.li>
                            </motion.ul>
                            
                            <motion.div variants={itemVariants} className={styles.buttonWrapper}>
                                <Link to="/about" className={styles.textLink} aria-label="Explore our approach">
                                    Explore Our Approach <FiArrowRight />
                                </Link>
                            </motion.div>
                        </motion.div>
                        
                        <motion.div 
                            className={styles.splitImageWrapper}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className={styles.imgOverlay}></div>
                            <img src={Image1} alt="Data network visualization" className={styles.splitImage} loading="lazy"/>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 6. INTEGRITY SECTION */}
            <section className={styles.integritySection}>
                <div className={styles.container}>
                    <div className={`${styles.splitGrid} ${styles.reversed}`}>
                         <motion.div 
                            className={styles.splitImageWrapper}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className={styles.imgOverlay}></div>
                            <img src={Image2} alt="System integrity lock" className={styles.splitImage} loading="lazy"/>
                        </motion.div>
                        
                        <motion.div 
                            className={styles.splitContent}
                            initial="hidden"
                            whileInView="visible"
                            variants={containerVariants}
                            viewport={{ once: true, margin: "-100px" }}
                        >
                            <motion.span variants={itemVariants} className={styles.sectionTag}>TRUST & ASSURANCE</motion.span>
                            <motion.h2 className={styles.splitTitle} variants={itemVariants}>
                                Uncompromising <br />
                                <span className={styles.titleGrey}>Data Integrity.</span>
                            </motion.h2>
                            <motion.p className={styles.splitText} variants={itemVariants}>
                                Trust is non-negotiable. Our platforms exceed global regulatory benchmarks with automated auditing trails.
                            </motion.p>
                            <motion.ul className={styles.featureList} variants={containerVariants}>
                                <motion.li variants={itemVariants}><FiCheck className={styles.checkIcon}/> ISO 27001 compliant models</motion.li>
                                <motion.li variants={itemVariants}><FiCheck className={styles.checkIcon}/> Real-time compliance monitoring</motion.li>
                            </motion.ul>
                            <motion.div variants={itemVariants} className={styles.buttonWrapper}>
                                <Link to="/contact" className={styles.textLink} aria-label="Request Security Whitepaper">
                                    Request Security Whitepaper <FiArrowRight />
                                </Link>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 7. PORTFOLIO SHOWCASE - UPDATED LAYOUT */}
            <AnimatedSection className={styles.portfolioSection}>
                <div className={styles.container}>
                    <motion.div className={styles.portfolioHeader} variants={itemVariants}>
                        <h2 className={styles.sectionTitleSmall}>Strategic Ventures</h2>
                        <Link to="/portfolio" className={styles.viewAllLink}>View All <FiArrowRight /></Link>
                    </motion.div>

                    <motion.div className={styles.ventureGrid} variants={containerVariants}>
                        {ventures.map((venture, index) => (
                             <motion.div key={index} variants={itemVariants}>
                                <Link to={venture.link} className={styles.ventureCard}>
                                    {/* Header Row: Icon + Title */}
                                    <div className={styles.ventureHeader}>
                                        <div className={styles.ventureIconBox}>
                                            {venture.icon}
                                        </div>
                                        <h3 className={styles.ventureTitle}>{venture.title}</h3>
                                    </div>
                                    {/* Body: Tagline */}
                                    <p className={styles.ventureTagline}>{venture.tagline}</p>
                                </Link>
                             </motion.div>
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>

            <section className={styles.finalCtaSection}>
                <div className={styles.container}>
                    <div className={styles.finalCtaGrid}>
                        <h2 className={styles.finalCtaTitle}>Ready to build the future?</h2>
                        <div className={styles.finalCtaActions}>
                            <Link to="/contact" className={styles.ctaPrimary}>Start a Project</Link>
                            <Link to="/about" className={styles.ctaGhost}>Read our Story</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;